import React from 'react';
import { Award, CheckCircle2 } from 'lucide-react';
import qualitycertification from '../assets/equipments/quality-certification.jpeg';

const Certification = () => {
  const certifications = [
    {
      title: 'ISO 9001:2015',
      description: 'Quality Management System certification ensuring consistent quality and continuous improvement.',
    },
    {
      title: 'API Certifications',
      description: 'American Petroleum Institute certifications for oilfield equipment manufacturing and testing.',
    },
    {
      title: 'ASME Certification',
      description: 'Authorized for pressure vessel and piping fabrication to ASME Boiler and Pressure Vessel Code.',
    },
    {
      title: 'AWS Certified Welders',
      description: 'American Welding Society certified welders and welding procedures for critical applications.',
    },
  ];

  const qualityProcesses = [
    'Incoming material inspection and verification',
    'In-process quality control checkpoints',
    'Final dimensional inspection',
    'Non-destructive testing (NDT)',
    'Hydrostatic pressure testing',
    'Complete documentation and traceability',
    'Certificate of Compliance (CoC) issuance',
    'Customer-specific quality requirements',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="certification-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden h-[30rem]" data-testid="certification-hero">
        <div className="absolute inset-0">
          <img
            src={qualitycertification}
            alt="Quality Certification"
            className="w-full h-full object-cover"
          />
           <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 pt-24">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="certification-heading">
            Quality Certification
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Committed to excellence through rigorous quality standards and industry certifications.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 heading-font mb-4">Our Certifications</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Accredited and certified to meet the most stringent industry standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8" data-testid={`certification-${index}`}>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 heading-font mb-2">{cert.title}</h3>
                    <p className="text-gray-600">{cert.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="quality-processes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 heading-font mb-4">Quality Assurance Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Every project follows our comprehensive quality control procedures.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <ul className="space-y-4">
              {qualityProcesses.map((process, index) => (
                <li key={index} className="flex items-start space-x-4 bg-white rounded-lg p-6" data-testid={`quality-process-${index}`}>
                  <CheckCircle2 className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg">{process}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="commitment-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6 text-center">Our Commitment to Quality</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            At Crow Corporation, quality is not just a goal—it's the foundation of everything we do. Our quality management system ensures that every project, from initial design through final delivery, meets or exceeds customer expectations and industry standards.
          </p>
          <p className="text-gray-600 leading-relaxed">
            We maintain comprehensive documentation and traceability for all materials and processes, providing complete transparency and accountability. Our team undergoes continuous training to stay current with the latest quality standards and best practices in manufacturing.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Certification;